'use client';

import { useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

import { useRetryFailedOcrBatchesMutation } from '../services/ocr.mutations';
import type { FailedPageRange } from '../types';

interface FailedBatchesCalloutProps {
  ocrResultId: number;
  totalBatches: number;
  failedBatches: number;
  failedPageRanges: FailedPageRange[];
}

/**
 * Surface the page ranges that failed in a `completed_with_errors` OCR run
 * alongside a "Retry failed batches" action. Re-runs only the unsuccessful
 * batches — successful pages keep their extracted fields and highlights.
 */
export function FailedBatchesCallout({
  ocrResultId,
  totalBatches,
  failedBatches,
  failedPageRanges
}: FailedBatchesCalloutProps) {
  const t = useTranslations('ocr');
  const retry = useRetryFailedOcrBatchesMutation();
  const [isRetrying, setIsRetrying] = useState(false);

  if (failedBatches === 0 || failedPageRanges.length === 0) {
    return null;
  }

  const rangeText = failedPageRanges
    .map((r) =>
      r.from === r.to
        ? String(r.from)
        : t('failedPageRange', { from: r.from, to: r.to })
    )
    .join(', ');

  const handleRetry = () => {
    setIsRetrying(true);
    retry.mutate(ocrResultId, {
      onSettled: () => setIsRetrying(false)
    });
  };

  return (
    <Alert
      variant='destructive'
      className='border-amber-500/50 text-amber-900 dark:text-amber-200 [&>svg]:text-amber-600'
    >
      <AlertTriangle className='h-4 w-4' />
      <AlertTitle>
        {t('failedBatchesTitle', {
          count: failedBatches,
          total: totalBatches
        })}
      </AlertTitle>
      <AlertDescription className='mt-1 flex flex-col gap-3'>
        <p>{t('failedPagesPlain', { ranges: rangeText })}</p>
        <p className='text-xs opacity-80'>{t('retryFailedBatchesHint')}</p>
        <div>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={handleRetry}
            disabled={isRetrying || retry.isPending}
          >
            {isRetrying || retry.isPending ? (
              <Loader2 className='me-2 h-3.5 w-3.5 animate-spin' />
            ) : (
              <RefreshCw className='me-2 h-3.5 w-3.5' />
            )}
            {t('retryFailedBatches')}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
